import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import iconLogo from '../../assets/images/logos/icononly_transparent_nobuffer.png';
import { usePlaidLinkToken } from '../../hooks/usePlaidLinkToken';

declare global {
  interface Window {
    Plaid?: any;
  }
}

interface PlaidConnectModalProps {
  open: boolean;
  onClose: () => void;
  onPlaidSuccess: (publicToken: string, metadata: any) => void;
  onPlaidExit?: (error: any, metadata: any) => void;
}

const PlaidConnectModal: React.FC<PlaidConnectModalProps> = ({
  open,
  onClose,
  onPlaidSuccess,
  onPlaidExit,
}) => {
  const { data: linkToken, isLoading, refetch } = usePlaidLinkToken(open);

  // Dynamically load Plaid Link script if needed
  useEffect(() => {
    if (!window.Plaid) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (open) {
        refetch();
    }
  }, [open, refetch]);

  const handleConnect = () => {
    if (!linkToken || !window.Plaid) return;
    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: onPlaidSuccess,
      onExit: onPlaidExit,
    });
    handler.open();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 0 }}>
        <Box
          component="img"
          src={iconLogo}
          alt="Dizpurse Icon"
          sx={{ height: 32, width: 32, mr: 1 }}
        />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Connect Your Bank
        </Typography>
        <IconButton onClick={onClose} edge="end" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Securely link your bank account to Dizpurse using Plaid.
        </Typography>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleConnect}
            disabled={!linkToken}
            sx={{
              mt: 2,
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(45deg, #3370FF 30%, #4318FF 90%)',
              boxShadow: '0 3px 8px 2px rgba(67, 24, 255, .12)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #4318FF 60%, #3370FF 100%)',
                boxShadow: '0 6px 12px 2px rgba(67, 24, 255, .18)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Connect to Plaid
          </Button>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Your credentials are never shared with Dizpurse.
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default PlaidConnectModal;
